import type {
  DetailedCircuitGeometry,
  TrackContribution,
  TrackContributionCornerApex,
  TrackContributionGpsCoord,
} from '@kangaroo/shared';
import { trackContributionHasGeometry } from '@kangaroo/shared';

import { lineMidpoint } from './detailedCircuitGeometry';
import {
  findNearestPolylineFraction,
  healCircuitPolylineForDisplay,
  closeManualLapDrawPolyline,
  isManualLapDrawClosed,
  type CornerApexSuggestion,
  type CurveType,
  type GpsCoord,
  type StartLineSuggestion,
} from './trackEnrichment';
import {
  lapSectionFeatures,
  type CatalogLapSection,
} from './trackCatalogGeometryBuilder';

export type TrackCatalogEditorLoadedState = {
  trackName: string;
  layoutName: string;
  notes: string;
  manualPolyline: GpsCoord[];
  lapClosed: boolean;
  customStartLine: StartLineSuggestion | null;
  manualCorners: CornerApexSuggestion[];
  sectorBoundaries: number[];
  trackWidthM: number;
  manualLapSections: CatalogLapSection[] | null;
  outsideLine: GpsCoord[];
  insideLine: GpsCoord[];
  catalogKerbRails: GpsCoord[][];
  kerbLines: GpsCoord[][];
  gridMarker: GpsCoord | null;
  distanceMarkersEnabled: boolean;
  buildDetailedOnSave: boolean;
  mapViewport: GpsCoord & { zoom?: number };
  contributionId: string;
  trackId: string;
  sessionKey: string;
  contributionStatus: TrackContribution['status'];
};

function toGpsCoord(point: TrackContributionGpsCoord): GpsCoord {
  return { lat: point.lat, lon: point.lon };
}

function cornerToSuggestion(corner: TrackContributionCornerApex): CornerApexSuggestion {
  return {
    id: corner.id,
    label: corner.label || `T${corner.turnNumber}`,
    lat: corner.lat,
    lon: corner.lon,
    turnNumber: corner.turnNumber,
    confidence: corner.confidence,
    curveType: corner.curveType as CurveType | undefined,
    detectionProfile: corner.detectionProfile as CornerApexSuggestion['detectionProfile'],
  };
}

function isClosedPolyline(polyline: GpsCoord[]) {
  return isManualLapDrawClosed(polyline);
}

function localMeters(left: GpsCoord, right: GpsCoord) {
  const latScale = 111_320;
  const lonScale = latScale * Math.cos(left.lat * Math.PI / 180);
  return Math.hypot((right.lon - left.lon) * lonScale, (right.lat - left.lat) * latScale);
}

export function estimateTrackWidthM(geometry?: DetailedCircuitGeometry | null) {
  const centerLine = geometry?.centerLine?.map(toGpsCoord) ?? [];
  const outline = geometry?.outlineRings?.[0]?.map(toGpsCoord) ?? [];
  if (centerLine.length < 2 || outline.length < 3) {
    return 10;
  }

  const sampleIndexes = [0.25, 0.5, 0.75].map(
    (fraction) => Math.floor(fraction * (centerLine.length - 1)),
  );
  let totalHalfWidth = 0;
  let count = 0;

  for (const index of sampleIndexes) {
    const sample = centerLine[index]!;
    let nearest = Number.POSITIVE_INFINITY;
    for (const point of outline) {
      nearest = Math.min(nearest, localMeters(sample, point));
    }
    if (Number.isFinite(nearest)) {
      totalHalfWidth += nearest;
      count += 1;
    }
  }

  const halfWidth = count > 0 ? totalHalfWidth / count : 5;
  return Math.max(6, Math.min(30, Math.round(halfWidth * 2)));
}

export function catalogLapSectionsFromDetailedGeometry(
  polyline: GpsCoord[],
  geometry: DetailedCircuitGeometry,
): CatalogLapSection[] {
  const sections = lapSectionFeatures(geometry);
  if (sections.length === 0) {
    return [];
  }

  return sections.map((feature) => {
    const type = feature.type.toLowerCase() === 'corner' ? 'corner' as const : 'straight' as const;
    const entryMid = lineMidpoint(feature.entryLine);
    const exitMid = lineMidpoint(feature.exitLine);
    const startFraction = entryMid
      ? findNearestPolylineFraction(polyline, entryMid).fraction
      : 0;
    const endFraction = exitMid
      ? findNearestPolylineFraction(polyline, exitMid).fraction
      : startFraction;

    return {
      id: feature.id,
      type,
      startFraction,
      endFraction,
      cornerId: type === 'corner' ? feature.id : undefined,
      entryLine: feature.entryLine?.map(toGpsCoord),
      exitLine: feature.exitLine?.map(toGpsCoord),
    };
  });
}

export function loadTrackCatalogEditorFromContribution(
  contribution: TrackContribution,
): TrackCatalogEditorLoadedState | null {
  const payload = contribution.payload;
  if (!payload.gpsPolyline || payload.gpsPolyline.length < 2) {
    return null;
  }

  const manualPolyline = payload.gpsPolyline.map(toGpsCoord);
  const lapClosed = isClosedPolyline(manualPolyline);
  const healedPolyline = healCircuitPolylineForDisplay(
    lapClosed ? closeManualLapDrawPolyline(manualPolyline) : manualPolyline,
  );
  const geometry = payload.detailedGeometry ?? null;
  const sectorBoundaries = payload.segmentSuggestions
    .slice(1)
    .map((segment) => segment.startFraction)
    .filter((fraction) => fraction > 0 && fraction < 1);
  const manualLapSections = geometry
    ? catalogLapSectionsFromDetailedGeometry(healedPolyline, geometry)
    : [];
  const center = healedPolyline[0] ?? manualPolyline[0]!;
  const featureByType = (type: string) => geometry?.features.filter(
    (feature) => feature.type.toLowerCase() === type.toLowerCase(),
  ) ?? [];
  const outlineRings = geometry?.outlineRings
    .filter((ring) => ring.length >= 3)
    .map((ring) => ring.map(toGpsCoord)) ?? [];
  const outsideLine = featureByType('TrackOutsideBoundary')[0]?.entryLine?.map(toGpsCoord) ?? [];
  const insideLine = featureByType('TrackInsideBoundary')[0]?.entryLine?.map(toGpsCoord) ?? [];
  const catalogKerbRails = outsideLine.length >= 3 && insideLine.length >= 3 ? [] : outlineRings;
  const kerbLines = featureByType('KerbMarker').flatMap((feature) => {
    if (feature.entryLine && feature.entryLine.length >= 2) {
      return [feature.entryLine.map(toGpsCoord)];
    }
    return feature.directionalPoint ? [[toGpsCoord(feature.directionalPoint)]] : [];
  });
  const gridMarkerFeature = featureByType('GridMarker')[0];

  return {
    trackName: contribution.venue?.trim() || '',
    layoutName: contribution.layoutName?.trim() || 'Main layout',
    notes: contribution.notes?.trim() || '',
    manualPolyline,
    lapClosed,
    customStartLine: payload.startLineSuggestion ?? null,
    manualCorners: payload.cornerApexSuggestions.map(cornerToSuggestion),
    sectorBoundaries,
    trackWidthM: estimateTrackWidthM(geometry),
    manualLapSections: manualLapSections.length > 0 ? manualLapSections : null,
    outsideLine,
    insideLine,
    catalogKerbRails,
    kerbLines,
    gridMarker: gridMarkerFeature?.directionalPoint ? toGpsCoord(gridMarkerFeature.directionalPoint) : null,
    distanceMarkersEnabled: featureByType('DistanceMarker').length > 0,
    buildDetailedOnSave: Boolean(
      geometry
      || payload.detailedGeometryVersion
      || payload.detailedGeometryChunkCount,
    ),
    mapViewport: { lat: center.lat, lon: center.lon, zoom: 17 },
    contributionId: contribution.id,
    trackId: contribution.trackId,
    sessionKey: contribution.sessionKey,
    contributionStatus: contribution.status,
  };
}

export function buildTrackCatalogEditUrl(contributionId: string) {
  const params = new URLSearchParams({
    mode: 'create',
    contribution: contributionId,
  });
  return `/admin/track-catalog?${params.toString()}`;
}

export function isMapAuthoredTrackContribution(contribution: TrackContribution) {
  if (!trackContributionHasGeometry(contribution.payload)) {
    return false;
  }

  const sessionKey = contribution.sessionKey.toLowerCase();
  return sessionKey.startsWith('track-catalog-create:')
    || sessionKey.includes('circuit-maker')
    || sessionKey.includes('manual-map')
    || Boolean(contribution.payload.detailedGeometryVersion)
    || Boolean(contribution.payload.detailedGeometryChunkCount);
}

export function formatSavedTrackContributionLabel(contribution: TrackContribution) {
  const venue = contribution.venue?.trim() || 'Untitled track';
  const layout = contribution.layoutName?.trim();
  const updated = new Date(contribution.updatedAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return layout ? `${venue} · ${layout} · ${updated}` : `${venue} · ${updated}`;
}

export function listSavedTrackContributionsForEditor(contributions: TrackContribution[] | undefined) {
  return (contributions ?? [])
    .filter((contribution) => trackContributionHasGeometry(contribution.payload))
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export function listMapAuthoredTrackContributions(contributions: TrackContribution[] | undefined) {
  return listSavedTrackContributionsForEditor(contributions)
    .filter(isMapAuthoredTrackContribution);
}
